import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import axios from 'axios';
import firebase from 'firebase/compat/app';
import { BehaviorSubject, take } from 'rxjs';
import Swal from 'sweetalert2';
import { KycService } from './modal-kyc/kyc.service';
import { ModalLoginService } from './modal-login.service';
import { ProjectStatusTransitionsService } from './project-status-transitions.service';
import { StorageEncryptionService } from './storage-encryption.service';
// No importamos directamente para evitar la dependencia circular
// import { TwoFactorAuthService } from './two-factor/two-factor-auth.service';

declare var $: any;
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loggedIn: any = new BehaviorSubject<boolean>(false);
  userAuth: any = new BehaviorSubject<any>(null);
  admin: any = new BehaviorSubject<any>(null);
  private kycService!: KycService | null;
  canWithdraw: boolean = false;
  userData: any = null;
  private mfaToken: string | null = null;

  private readonly cloudUrl =
    'https://us-central1-domoblock-dapp.cloudfunctions.net/resetUserPassword'; // Produccion
  // private cloudUrl = 'https://us-central1-domoblock-devnew.cloudfunctions.net/resetUserPassword'; // test
  private readonly apiUrl = 'https://sendmail.domoblock.io'; // Reemplaza con la URL de tu API
  private readonly userDataStorageKey = 'domoblock_user_data'; // Clave para localStorage

  constructor(
    private readonly auth: AngularFireAuth,
    private readonly afs: AngularFirestore,
    private readonly fns: AngularFireFunctions,
    private readonly afAuth: AngularFireAuth,
    private readonly router: Router,
    private readonly modal: NgbModal,
    private readonly injector: Injector,
    private readonly modalAuth: ModalLoginService,
    private readonly projectStatusTransitionsService: ProjectStatusTransitionsService,
    private readonly storageEncryption: StorageEncryptionService,
  ) {
    // Forzando creación de ProjectStatusTransitionsService
  }

  // Guardar datos del usuario encriptados en localStorage
  private saveUserDataToStorage(userData: any): void {
    this.storageEncryption.saveEncryptedData(this.userDataStorageKey, userData);
  }

  // Obtener datos del usuario desencriptados desde localStorage
  getUserDataFromStorage(): any {
    return this.storageEncryption.getDecryptedData(this.userDataStorageKey);
  }

  // Limpiar datos del usuario del localStorage
  clearUserDataFromStorage(): void {
    this.storageEncryption.removeData(this.userDataStorageKey);
  }

  // Verificar si existen datos del usuario en localStorage
  hasUserDataInStorage(): boolean {
    return this.storageEncryption.hasData(this.userDataStorageKey);
  }

  // Limpiar todos los datos de DomoBlock del localStorage
  clearAllStorageData(): void {
    this.storageEncryption.clearAllDomoblockData();
  }

  /** Abre el modal KYC tras login. Debe llamarse después de navegar (p. ej. en .then() del router) para que la vista de destino esté lista. */
  openKyc() {
    setTimeout(() => {
      this.kycService = this.injector.get(KycService);
      if (this.kycService) {
        this.kycService.openModalKyc();
      }
    }, 400);
  }

  async isLoggedIn(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.afAuth.authState.pipe(take(1)).subscribe((userAuth: any) => {
        if (userAuth) {
          this.userAuth.next(userAuth);
          this.loggedIn.next(true);
          this.checkAdmin();

          this.storageEncryption.setEncryptionKey(userAuth.uid);

          // ✅ Iniciar monitoreo de proyectos cuando el usuario se autentica
          this.projectStatusTransitionsService.startAutomaticMonitoring();

          resolve(true);
        } else {
          this.userAuth.next(null);
          this.loggedIn.next(false);
          resolve(false);
        }
      });
    });
  }

  registerWithEmail(email: string, password: string) {
    Swal.fire({
      allowOutsideClick: false,
      icon: 'info',
      text: 'Enviando los datos ...',
    });
    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then((resProfile: any) => {
        resProfile.user
          .sendEmailVerification()
          .then(() => {
            Swal.fire({
              imageUrl: '../../assets/img/email.png',
              imageAlt: 'Email Img',
              title: '¡Revisa tu correo!',
              html:
                '<p style="color: #292929">Verifica tu email a través del correo que te hemos enviado para completar tu registro en Domoblock. Si no lo ves, recuerda revisar la carpeta de spam.</p>' +
                '<br><br><label style="color: #292929">¿No lo has recibido? <span style="color: #0093A2; cursor: pointer;" id="resendEmail">Enviar de nuevo</span></label>',
              showConfirmButton: false,
              allowOutsideClick: false,
              allowEscapeKey: false,
              showCloseButton: true,
            }).then(() => {
              Swal.close();
              this.logout();
              // this.modalAuth.closeModalRegister();
              this.modalAuth.closeModalRegister();
              this.modalAuth.openModalLogin();
            });

            const resendEmailElement = document.getElementById('resendEmail');
            if (resendEmailElement) {
              resendEmailElement.addEventListener('click', () => {
                resProfile.user.sendEmailVerification();
                Swal.fire({
                  icon: 'success',
                  text: '¡Email enviado!',
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                }).then(() => {
                  this.logout();
                  this.router.navigateByUrl('/login');
                });
              });
            }
          })
          .catch(() => {
            firebase.auth().currentUser?.delete();

            Swal.close();
            Swal.fire({
              icon: 'error',
              text: '¡Algo ha ido mal! Inténtalo más tarde.',
            });

            this.logout();
          });
      })
      .catch((error: any) => {
        Swal.close();
        if (error.code === 'auth/email-already-in-use') {
          Swal.fire({
            icon: 'error',
            text: '¡Este email ya existe!',
          }).then(() => {
            Swal.close();
            this.logout();
          });
        } else {
          Swal.fire({
            icon: 'error',
            text: '¡Algo ha ido mal! Inténtalo más tarde.',
          }).then(() => {
            Swal.close();
            this.logout();
          });
        }
      });
  }

  async signInWithGoogle() {
    await this.afAuth.setPersistence('local'); // Habilita la persistencia de sesión
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    googleProvider.setCustomParameters({ prompt: 'select_account' });
    await this.afAuth
      .signInWithPopup(googleProvider)
      .then((res: any) => {
        this.modalAuth.closeModalLogin();
        // Obtener el servicio TwoFactorAuthService a través del injector
        // solo cuando sea necesario
        import('./two-factor/two-factor-auth.service')
          .then((module) => {
            const twoFactorAuthService = this.injector.get(
              module.TwoFactorAuthService,
            );

            // Verificar si el usuario tiene 2FA habilitado
            twoFactorAuthService
              .isEnabled(res.user.uid)
              .then((has2FAEnabled) => {
                if (has2FAEnabled) {
                  // Extraer el token de inmediato
                  res.user.getIdToken(true).then((token: string) => {
                    // Guardar información temporal para la verificación 2FA
                    sessionStorage.setItem(
                      'pending2FAAuth',
                      JSON.stringify({
                        uid: res.user.uid,
                        email: res.user.email,
                        initialToken: token
                      }),
                    );

                    // Redirigir a la página de verificación 2FA
                    this.router.navigateByUrl('/oportunidades');
                  });
                  return;
                }

                // Continuar con el flujo normal si no hay 2FA
                this.afs
                  .collection('users')
                  .doc(res.user.uid)
                  .get()
                  .subscribe((userDoc: any) => {
                    if (userDoc.exists) {
                      this.fns
                        .httpsCallable('addCustomClaims')({})
                        .subscribe((res: any) => {
                          res.success ? this.checkAdmin() : null;
                        });

                      const user = userDoc.data();

                      this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
                    } else {
                      this.createUser(
                        res.user.uid,
                        res.user.email,
                        res.user.displayName,
                      );
                    }
                  });
              })
              .catch((error) => {
                // FAIL-CLOSED: Si la validación de 2FA explota, abortar inicio de sesión
                Swal.fire({
                  icon: 'error',
                  text: 'Error de Seguridad: No se pudo verificar el estado de factor doble (2FA). Por seguridad, inicio de sesión abortado.'
                });
                this.logout();
              });
          })
          .catch((err) => {
            // Si hay error al cargar el servicio, continuar con flujo normal
            this.afs
              .collection('users')
              .doc(res.user.uid)
              .get()
              .subscribe((userDoc: any) => {
                if (userDoc.exists) {
                  this.fns
                    .httpsCallable('addCustomClaims')({})
                    .subscribe((res: any) => {
                      res.success ? this.checkAdmin() : null;
                    });

                  const user = userDoc.data();

                  this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
                } else {
                  this.createUser(
                    res.user.uid,
                    res.user.email,
                    res.user.displayName,
                  );
                }
              });
          });
      });
  }

  async registerWithGoogle() {
    try {
      const googleProvider = new firebase.auth.GoogleAuthProvider();
      googleProvider.setCustomParameters({ prompt: 'select_account' });
      await this.afAuth
        .signInWithPopup(googleProvider)
        .then((res: any) => {
          this.modalAuth.closeModalRegister();

          // Obtener el servicio TwoFactorAuthService a través del injector
          import('./two-factor/two-factor-auth.service')
            .then((module) => {
              const twoFactorAuthService = this.injector.get(
                module.TwoFactorAuthService,
              );

              // Verificar si el usuario tiene 2FA habilitado
              twoFactorAuthService
                .isEnabled(res.user.uid)
                .then((has2FAEnabled) => {
                  if (has2FAEnabled) {
                    // Extraer token de inmediato
                    res.user.getIdToken(true).then((token: string) => {
                      // Guardar información temporal para la verificación 2FA
                      sessionStorage.setItem(
                        'pending2FAAuth',
                        JSON.stringify({
                          uid: res.user.uid,
                          email: res.user.email,
                          initialToken: token
                        }),
                      );

                      // Redirigir a la página de oportunidades donde saltará el modal
                      this.router.navigateByUrl('/oportunidades');
                    });
                    return;
                  }

                  // Continuar con el flujo normal si no hay 2FA
                  this.afs
                    .collection('users')
                    .doc(res.user._delegate.uid)
                    .get()
                    .subscribe((userDoc: any) => {
                      if (userDoc.exists) {
                        this.fns
                          .httpsCallable('addCustomClaims')({})
                          .subscribe((res: any) => {
                            res.success ? this.checkAdmin() : null;
                          });

                        const user = userDoc.data();
                        this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
                      } else {
                        this.createUser(
                          res.user.uid,
                          res.user.email,
                          res.user.displayName,
                        );
                      }
                    });
                })
                .catch((error) => {
                  // FAIL-CLOSED: Si la validación de 2FA explota, abortar inicio de sesión
                  Swal.fire({
                    icon: 'error',
                    text: 'Error de Seguridad: No se pudo verificar el estado de factor doble (2FA). Por seguridad, inicio de sesión abortado.'
                  });
                  this.logout();
                });
            })
            .catch((err) => {
              // Continuar con flujo normal si hay error
              this.afs
                .collection('users')
                .doc(res.user._delegate.uid)
                .get()
                .subscribe((userDoc: any) => {
                  if (userDoc.exists) {
                    this.fns
                      .httpsCallable('addCustomClaims')({})
                      .subscribe((res: any) => {
                        res.success ? this.checkAdmin() : null;
                      });

                    const user = userDoc.data();
                    this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
                  } else {
                    this.createUser(
                      res.user.uid,
                      res.user.email,
                      res.user.displayName,
                    );
                  }
                });
            });
        });
    } catch (error) {
      // Error en registro con Google
    }
  }

  saveUserdata(userId: string) {
    this.afs
      .collection('users')
      .doc(userId)
      .get()
      .subscribe((userRef: any) => {
        this.userData = userRef.exists ? userRef.data() : null;

        // Guardar datos del usuario encriptados en localStorage
        if (this.userData) {
          this.saveUserDataToStorage(this.userData);
        }
      });
  }

  async usercanWithdraw(userData?: any): Promise<boolean> {
    userData = userData ?? this.getUserDataFromStorage();
    return (
      (!!userData?.particular && !!userData?.particular.ciudad) ||
      (!!userData?.Empresa && !!userData?.Empresa.Representante.ciudad)
    );
  }

  login(email: string, password: string) {
    firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL)
      .then(() => {
        return firebase.auth().signInWithEmailAndPassword(email, password);
      })
      .then((res: any) => {
        if (!res.user.emailVerified) {
          this.sendOtherEmailVerification();
          return;
        }

        // Obtener el servicio TwoFactorAuthService a través del injector
        // solo cuando sea necesario
        import('./two-factor/two-factor-auth.service')
          .then((module) => {
            const twoFactorAuthService = this.injector.get(
              module.TwoFactorAuthService,
            );

            // Verificar si el usuario tiene 2FA habilitado usando el servicio
            twoFactorAuthService
              .isEnabled(res.user.uid)
              .then((has2FAEnabled) => {
                if (has2FAEnabled) {
                  // Cerrar modal de login antes de redirigir
                  this.modalAuth.closeModalLogin();

                  // Extraer de inmediato el token de res.user para evadir fallos de estado
                  res.user.getIdToken(true).then((token: string) => {
                    // Guardar información temporal para la verificación 2FA
                    sessionStorage.setItem(
                      'pending2FAAuth',
                      JSON.stringify({
                        uid: res.user.uid,
                        email: res.user.email,
                        initialToken: token
                      }),
                    );

                    // Redirigir a la página de verificación 2FA
                    this.router.navigateByUrl('/oportunidades');
                  });
                  return;
                }

                // Continuar con el flujo normal si no hay 2FA
                this.modalAuth.closeModalLogin();

                this.fns
                  .httpsCallable('addCustomClaims')({})
                  .subscribe((claimRes: any) => {
                    claimRes.success ? this.checkAdmin() : null;
                  });

                this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
              })
              .catch((error) => {
                // FAIL-CLOSED: Si la validación de 2FA explota, abortar inicio de sesión
                this.modalAuth.closeModalLogin();
                Swal.fire({
                  icon: 'error',
                  text: 'Error de Seguridad: No se pudo verificar el estado de factor doble (2FA). Por seguridad, inicio de sesión abortado.'
                });
                this.logout();
              });
          })
          .catch((err) => {
            // Si hay error al cargar el servicio, continuar con login normal
            this.modalAuth.closeModalLogin();

            this.fns
              .httpsCallable('addCustomClaims')({})
              .subscribe((claimRes: any) => {
                claimRes.success ? this.checkAdmin() : null;
              });
            this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
          });
      })
      .catch((error: any) => {
        if (error.code === 'auth/user-not-found') {
          Swal.fire({
            icon: 'error',
            text: 'El correo electrónico ingresado no está registrado.',
          }).then(() => {
            Swal.close();
            this.logout();
            this.openLoginForm();
          });
        } else if (error.code === 'auth/wrong-password') {
          Swal.fire({
            icon: 'error',
            text: 'La contraseña ingresada es incorrecta.',
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            Swal.close();
            this.logout();
            this.openLoginForm();
          });
        } else if (error.code === 'auth/invalid-login-credentials') {
          Swal.fire({
            icon: 'error',
            text: 'El correo o la contraseña ingresada son incorrectos.',
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            Swal.close();
            this.logout();
            this.openLoginForm();
          });
        } else {
          Swal.fire({
            icon: 'error',
            text: '¡Algo ha ido mal! Inténtalo más tarde.',
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            Swal.close();
            this.logout();
            this.openLoginForm();
          });
        }
      });
  }

  // Método para completar el login después de verificación 2FA
  completeLoginAfter2FA(uid: string) {
    // Limpiar datos temporales
    sessionStorage.removeItem('pending2FAAuth');

    // Continuar con el flujo normal
    this.fns
      .httpsCallable('addCustomClaims')({})
      .subscribe((res: any) => {
        res.success ? this.checkAdmin() : null;
      });

    this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
  }

  async checkAdmin() {
    firebase
      .auth()
      .currentUser?.getIdTokenResult(true)
      .then((res: any) => {
        res.claims.admin !== undefined && res.claims.admin !== null
          ? this.admin.next(res.claims.admin)
          : this.admin.next(false);
      });
  }

  async logout() {
    // Limpiar datos del usuario del localStorage al cerrar sesión
    this.clearUserDataFromStorage();
    this.mfaToken = null;
    // Limpiar flag del modal de certificado para que se muestre de nuevo tras el próximo login
    sessionStorage.removeItem('residenceCertificateModalShown');
    await firebase.auth().signOut();
  }

  reload() {
    location.reload();
  }

  sendOtherEmailVerification() {
    Swal.fire({
      imageUrl: '../../assets/img/email.png',
      imageAlt: 'Email Img',
      title: '¡Correo electrónico sin verificar!',
      html:
        '<p style="color: #292929">Para acceder a la plataforma de inversión, antes debes verificar tu correo. Si no lo ves, recuerda revisar la carpeta de spam.</p>' +
        '<br><br><span style="color: #0093A2; cursor: pointer;" id="resendEmail">Enviar correo de verificación de nuevo.</span>',
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showCloseButton: true,
    }).then(() => {
      Swal.close();
      this.logout();
    });

    const resendEmailElement = document.getElementById('resendEmail');
    if (resendEmailElement) {
      resendEmailElement.addEventListener('click', () => {
        firebase.auth().currentUser?.sendEmailVerification();
        Swal.fire({
          icon: 'success',
          text: '¡Email enviado!',
          allowOutsideClick: false,
          allowEscapeKey: false,
        })
          .then(() => {
            Swal.close();
            this.logout();
          })
          .catch(() => {
            firebase.auth().currentUser?.delete();
            Swal.close();
            Swal.fire({
              icon: 'error',
              text: '¡Algo ha ido mal! Inténtalo más tarde.',
            }).then(() => {
              Swal.close();
              this.logout();
            });
          });
      });
    }
  }

  resetPassword(email: string) {
    firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        Swal.close();
        Swal.fire({
          icon: 'success',
          text: 'Te hemos enviado un email',
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          Swal.close();
          this.logout();
        });
      })
      .catch(() => {
        Swal.close();
        Swal.fire({
          icon: 'error',
          text: `Algo ha ido mal... Parece que el email "${email}" es erróneo.`,
          allowOutsideClick: false,
          allowEscapeKey: false,
        }).then(() => {
          Swal.close();
          this.logout();
        });
      });
  }

  createUser = async (uid: string, email: string, displayName: string) => {
    try {
      await this.afs
        .collection('users')
        .doc(uid)
        .set({ uid, email, displayName }, { merge: true });
      // Registrar en pre-registerdata para tener evidencia y que el KYC se muestre correctamente
      await this.afs
        .collection('pre-registerdata')
        .doc(uid)
        .set(
          {
            userdata: { registeredVia: 'google' },
            step: 0,
            stage: 0,
          },
          { merge: true }
        );
      this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
    } catch (error: any) {
      if (error.code !== 'permission-denied') {
        Swal.fire({
          icon: 'error',
          text: '¡Algo ha ido mal! Inténtalo más tarde.',
        }).then(() => {
          Swal.close();
          this.logout();
        });
      } else {
        this.router.navigateByUrl('/oportunidades').then(() => this.openKyc());
      }
    }
  };

  openRegisterForm() {
    this.modalAuth.openModalRegister();
  }

  openLoginForm() {
    this.modalAuth.openModalLogin();
  }

  closeModalLogin() {
    this.modalAuth.closeModalLogin();
  }

  closeModalRegister() {
    this.modalAuth.closeModalRegister();
  }

  forgotPassword(userEmail: string, newPassword: string, otp: string) {
    return axios.post(this.cloudUrl, { userEmail, newPassword, otp });
  }

  sentOtpforResetPassword(email: string) {
    return axios.post(`${this.apiUrl}/notifications/create/OTP`, { email });
  }

  /** Almacena el ID Token refrescado inmediatamente después de verificar 2FA.
   *  Garantiza que el token lleva las claims mfa:true y mfa_verified_at frescos. */
  async storeMFAToken(): Promise<void> {
    const user = firebase.auth().currentUser;
    if (user) {
      this.mfaToken = await user.getIdToken(true);
    }
  }

  /** Devuelve el token MFA almacenado. Usar en lugar de getJWTToken() para endpoints
   *  protegidos por MfaGuard o MfaFreshGuard. */
  getMFAToken(): string | null {
    return this.mfaToken;
  }

  async getJWTToken(): Promise<string | undefined> {    try {
      // Prioridad 1: token capturado en login (evita esperar authState)
      try {
        const authData = sessionStorage.getItem('pending2FAAuth');
        if (authData) {
          const parsed = JSON.parse(authData);
          if (parsed?.initialToken) {
            return parsed.initialToken;
          }
        }
      } catch (e) {}

      const user = firebase.auth().currentUser;
      if (user) {
        return await user.getIdToken(true);
      }
      
      // Fallback: Esperar hasta 2 segundos a que Firebase inicialice la sesión
      return new Promise((resolve) => {
        const unsubscribe = firebase.auth().onAuthStateChanged(async (u) => {
          if (u) {
            const token = await u.getIdToken(true);
            unsubscribe();
            resolve(token);
          }
        });
        
        setTimeout(() => {
          unsubscribe();
          
          try {
            const authData = sessionStorage.getItem('pending2FAAuth');
            if (authData) {
              const parsed = JSON.parse(authData);
              if (parsed && parsed.initialToken) {
                resolve(parsed.initialToken);
                return;
              }
            }
          } catch (e) {}
          
          resolve(undefined);
        }, 2000);
      });
      
    } catch (e) {
      console.error('[getJWTToken] Error obteniendo usuario:', e);
      return undefined;
    }
  }
}
